// Shared plan catalog for the dashboard frontend. Mirrors the lander's
// PLAN_CATALOG (unbreakable-lander/src/lib/plans.ts) in shape but is NOT
// imported across repos — the lander catalog drives the funnel checkout
// display, this one drives in-app subscription UI. Keep prices in lockstep
// with Stripe (verify via scripts/verify-plan-prices.mjs on the lander side).

import type { SubscriptionPlan } from './auth-client'

export interface PlanTier {
  slug: SubscriptionPlan
  displayName: string
  priceCurrentUsd: number
  cadenceLabel: string
  /** For weekly_trial: the slug the subscription converts to after the trial. */
  conversionPlanSlug?: SubscriptionPlan
}

export const PLANS_CATALOG: Record<SubscriptionPlan, PlanTier> = {
  weekly_trial: {
    slug: 'weekly_trial',
    displayName: '1-Week Trial',
    priceCurrentUsd: 2.87,
    cadenceLabel: 'one-time',
    conversionPlanSlug: 'monthly',
  },
  monthly: {
    slug: 'monthly',
    displayName: '4-Week Plan',
    priceCurrentUsd: 29.49,
    cadenceLabel: 'every 4 weeks',
  },
  quarterly: {
    slug: 'quarterly',
    displayName: '12-Week Plan',
    priceCurrentUsd: 69.99,
    cadenceLabel: 'every 12 weeks',
  },
  yearly: {
    slug: 'yearly',
    displayName: 'Yearly Plan',
    priceCurrentUsd: 59.99,
    cadenceLabel: 'per year',
  },
}

export function getPlanTier(slug: SubscriptionPlan | null | undefined): PlanTier | null {
  if (!slug) return null
  return PLANS_CATALOG[slug] ?? null
}

export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`
}

/**
 * For a plan that has a trial phase, returns the plan users convert to when
 * the trial ends. Returns the same plan if it has no trial phase.
 */
export function getConversionPlan(slug: SubscriptionPlan | null | undefined): PlanTier | null {
  const tier = getPlanTier(slug)
  if (!tier) return null
  if (tier.conversionPlanSlug) return getPlanTier(tier.conversionPlanSlug)
  return tier
}
